import DOMPurify from "dompurify";
import type { Matrix } from "~/lib/svgEditor/matrix";
import type { Box } from "~/lib/svgEditor/selection";
import { multiply, scaleFactor } from "~/lib/svgEditor/matrix";

/** The elements that count as shapes, in the order they are drawn. */
export const SHAPE_SELECTOR = "path, polygon, polyline, rect, circle, ellipse";

/** Where a shape is not drawn but only defined — a mask, a clip, a pattern. */
const DEFINITIONS_SELECTOR =
    "defs, mask, clipPath, clippath, pattern, marker, symbol";

/**
 * What to change on a shape: attributes and inline style properties, where
 * `undefined` takes one away; how far to cut the shape in (zero for not at
 * all); or the shape itself, taken out of the document.
 */
export type ShapeEdit = {
    attributes?: Record<string, string | undefined>;
    inset?: number;
    remove?: boolean;
    style?: Record<string, string | undefined>;
};

/**
 * Where a shape lies: its box in the document's user units, how its parent's
 * coordinates map into the document's, and its own `transform`.
 */
export type ShapeGeometry = {
    box: Box;
    parentToRoot: Matrix;
    transform: Matrix;
};

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

/** What gives a shape its geometry; everything else is paint. */
export const GEOMETRY_ATTRIBUTES = [
    "cx",
    "cy",
    "d",
    "fill-rule",
    "height",
    "points",
    "r",
    "rx",
    "ry",
    "width",
    "x",
    "y",
];

/** The masks the editor cuts shapes in with are named so. */
const INSET_ID_PREFIX = "svg-editor-inset-";

/**
 * A shape's bare geometry: an element of the same kind with only what shapes
 * it — no paint, no transform, no id.
 */
export function bareGeometry(shape: Element): Element {
    const bare = shape.ownerDocument.createElementNS(
        SVG_NAMESPACE,
        shape.tagName.toLowerCase(),
    );

    for (const name of GEOMETRY_ATTRIBUTES) {
        const value = shape.getAttribute(name);

        if (value !== null) bare.setAttribute(name, value);
    }

    return bare;
}

/** How much larger the shape's own units appear in the document. */
export function documentScale(geometry: ShapeGeometry): number {
    return scaleFactor(multiply(geometry.parentToRoot, geometry.transform));
}

/**
 * The shapes that are drawn, in document order — those inside definitions
 * (such as the editor's own inset masks) do not count. Every index of a
 * shape, in the editor and in its edits, refers to this list.
 */
export function drawnShapes(scope: ParentNode): Element[] {
    return [...scope.querySelectorAll(SHAPE_SELECTOR)].filter(
        (shape) => shape.closest(DEFINITIONS_SELECTOR) === null,
    );
}

/**
 * The document with the given shapes changed — by their index in document
 * order, the same order the editor's document counts in. Comes back as XML,
 * ready to be used as a file. `sanitized` when `svg` is known to be clean
 * already — what an earlier edit gave — which saves cleaning it again.
 */
export function editShapes(
    svg: string,
    edits: ReadonlyMap<number, ShapeEdit>,
    sanitized = false,
): string {
    const parsed = new DOMParser().parseFromString(
        sanitized ? svg : sanitizeSvg(svg),
        "text/html",
    );
    const root = parsed.body.querySelector("svg");

    if (!root) return svg;

    const shapes = drawnShapes(parsed.body);

    for (const [index, edit] of edits) {
        const shape = shapes.at(index);

        if (!shape) continue;

        if (edit.remove) {
            removeShape(shape);

            continue;
        }

        for (const [name, value] of Object.entries(edit.attributes ?? {})) {
            if (value === undefined) {
                shape.removeAttribute(name);
            } else {
                shape.setAttribute(name, value);
            }
        }

        const style = (shape as HTMLElement | SVGElement).style;

        for (const [name, value] of Object.entries(edit.style ?? {})) {
            if (value === undefined) {
                style.removeProperty(name);
            } else {
                style.setProperty(name, value);
            }
        }

        if (shape.getAttribute("style") === "") shape.removeAttribute("style");

        if (edit.inset !== undefined) setInset(root, shape, edit.inset);
    }

    return new XMLSerializer().serializeToString(root);
}

/**
 * How far the editor's mask cuts `shape` in, in the shape's own units; zero
 * without one.
 */
export function insetOf(shape: Element): number {
    const stroked = insetMaskOf(shape)?.querySelector("[stroke-width]");
    const width = Number.parseFloat(
        stroked?.getAttribute("stroke-width") ?? "",
    );

    return Number.isFinite(width) && width > 0 ? width / 2 : 0;
}

/** Takes `shape` out of its document, and the editor's inset mask on it with it. */
export function removeShape(shape: Element): void {
    insetMaskOf(shape)?.remove();
    shape.remove();
}

/** The document as the editor shows and edits it: scripts and the like gone. */
export function sanitizeSvg(svg: string): string {
    return DOMPurify.sanitize(svg, {
        USE_PROFILES: { svg: true, svgFilters: true },
    });
}

/**
 * Cuts `shape` in by `inset` (its own units): a mask of the shape's own
 * geometry whose edge is stroked away by twice the inset — the half outside
 * the shape does nothing. Zero takes the mask away again.
 */
export function setInset(root: Element, shape: Element, inset: number): void {
    const existing = insetMaskOf(shape);

    if (inset <= 0) {
        existing?.remove();
        shape.removeAttribute("mask");

        return;
    }

    let mask = existing;

    if (!mask) {
        let defs = root.querySelector(":scope > defs");

        if (!defs) {
            defs = root.ownerDocument.createElementNS(SVG_NAMESPACE, "defs");
            root.prepend(defs);
        }

        mask = root.ownerDocument.createElementNS(SVG_NAMESPACE, "mask");
        mask.id = nextInsetId(root);
        defs.append(mask);
        shape.setAttribute("mask", `url(#${mask.id})`);
    }

    const edge = bareGeometry(shape);

    edge.setAttribute("fill", "#fff");
    edge.setAttribute("stroke", "#000");
    edge.setAttribute("stroke-linejoin", "round");
    edge.setAttribute("stroke-width", String(Number((2 * inset).toFixed(4))));
    mask.replaceChildren(edge);
}

/** The editor's inset mask on `shape`, if it has one. */
function insetMaskOf(shape: Element): Element | undefined {
    const id = /^url\(["']?#([^"')]+)["']?\)$/.exec(
        shape.getAttribute("mask") ?? "",
    )?.[1];

    if (!id?.startsWith(INSET_ID_PREFIX)) return;

    return shape.ownerDocument.querySelector(`[id="${id}"]`) ?? undefined;
}

/** An id for an inset mask that no other mask of the document has. */
function nextInsetId(root: Element): string {
    const taken = new Set(
        [...root.querySelectorAll("mask[id]")].map((mask) => mask.id),
    );
    let number = 1;

    while (taken.has(`${INSET_ID_PREFIX}${String(number)}`)) number++;

    return `${INSET_ID_PREFIX}${String(number)}`;
}
